import {
  SnowplowEcommercePlugin,
  trackProductView,
  trackAddToCart,
  trackRemoveFromCart,
  trackProductListView,
  trackProductListClick,
  trackPromotionView,
  trackPromotionClick,
  trackCheckoutStep,
  trackTransaction,
} from '@snowplow/browser-plugin-snowplow-ecommerce';

export { SnowplowEcommercePlugin };


export {
  trackRefund,
  trackTransactionError,
  setPageType,
  setEcommerceUser,
} from '@snowplow/browser-plugin-snowplow-ecommerce';


function parsePrice(value) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (!value) {
    return 0;
  }

  return Number(String(value).replace(/[^0-9.-]+/g, '')) || 0;
}

function cartValue(products) {
  return (products || []).reduce((total, product) => {
    const price = parsePrice(product.price);
    const quantity = Number(product.quantity) || 1;

    return total + price * quantity;
  }, 0);
}

function paymentMethod(purchase) {
  return (
    purchase?.actionField?.payment_method ||
    purchase?.actionField?.paymentMethod ||
    purchase?.payment_method ||
    purchase?.paymentMethod ||
    'unknown'
  );
}

function getCurrency(ecommerce, products) {
  if (ecommerce?.currencyCode) {
    return ecommerce.currencyCode;
  }

  const list = products || [];
  const looksLikeUsd = list.length > 0 && list.every(
    (product) => typeof product?.price === 'string' && product.price.includes('$')
  );

  return looksLikeUsd ? 'USD' : undefined;
}


function toNativeProduct(product, currency) {
  const rawVariant =
   product?.variant ?? product?.item_variant ?? null;

  const variant =
    typeof rawVariant === 'string'
      ? rawVariant
      : null;
 
  return {
    id: product.id || product.item_id || '',
    name: product.name || product.item_name || '',
    brand: product.brand || product.item_brand || '',
    category: product.category || product.item_category || '',
    variant,
    price: parsePrice(product.price),
    currency,
    quantity: Number(product.quantity) || 1,
  };
}

function toNativePromotion(promo) {
  return {
    id: promo.id,
    name: promo.name,
    position: promo.position !== undefined ? Number(promo.position) : undefined,
  };
}

const seenProductViews = new Set();

function trackViewEventsFromDataLayer(data) {
  if (data?.event === 'checkout_progress' && Array.isArray(data.items) && data.items.length) {
    const products = data.items;
    const currency = getCurrency(data.ecommerce, products);

    let step;

    if (typeof data.ecommerce?.checkout?.actionField?.step === 'number') {
      step = data.ecommerce.checkout.actionField.step;
    } else if (typeof data.checkout_step === 'string') {
      const match = data.checkout_step.match(/\d+/);

      if (match) {
        step = Number(match[0]);
      }
    }

    if (typeof step === 'number') {
      trackCheckoutStep({ step });
    }

    products.forEach((product) => {
      trackProductView(toNativeProduct(product, currency));
    });
  }

  const ecommerce = data?.ecommerce;

  if (!ecommerce) {
    return;
  }

  // Product view - sourced from Enhanced Ecommerce's product detail action
  if (Array.isArray(ecommerce.detail?.products)) {
    const currency = getCurrency(ecommerce, ecommerce.detail.products);

    ecommerce.detail.products.forEach((product) => {
      const nativeProduct = toNativeProduct(product, currency);

      const key = [
        nativeProduct.id,
        nativeProduct.name,
        nativeProduct.price,
      ].join('|');

      if (seenProductViews.has(key)) {
        return;
      }

      seenProductViews.add(key);

      trackProductView(nativeProduct);
    });
  }

  // Product list view
  if (Array.isArray(ecommerce.impressions) && ecommerce.impressions.length) {
    const currency = getCurrency(ecommerce, ecommerce.impressions);

    trackProductListView({
      name: ecommerce.impressions[0]?.list || '',
      products: ecommerce.impressions.map((product) => toNativeProduct(product, currency)),
    });
  }

  // Product list click
  if (Array.isArray(ecommerce.click?.products) && ecommerce.click.products[0]) {
    const product = ecommerce.click.products[0];
    const currency = getCurrency(ecommerce, [product]);

    trackProductListClick({
      name: product.list || '',
      product: toNativeProduct(product, currency),
    });
  }

  // Promotion view
  if (Array.isArray(ecommerce.promoView?.promotions)) {
    ecommerce.promoView.promotions.forEach((promo) => {
      trackPromotionView(toNativePromotion(promo));
    });
  }

  // Promotion click
  if (Array.isArray(ecommerce.promoClick?.promotions)) {
    ecommerce.promoClick.promotions.forEach((promo) => {
      trackPromotionClick(toNativePromotion(promo));
    });
  }
}


function trackActionEventsFromDataLayer(data) {
  const ecommerce = data?.ecommerce;

  if (!ecommerce) {
    return;
  }

  // Add to cart
  if (Array.isArray(ecommerce.add?.products)) {
    const currency = getCurrency(ecommerce, ecommerce.add.products);

    trackAddToCart({
      products: ecommerce.add.products.map((product) => toNativeProduct(product, currency)),
      total_value: cartValue(ecommerce.add.products),
      currency,
    });
  }

  // Remove from cart
  if (Array.isArray(ecommerce.remove?.products)) {
    const currency = getCurrency(ecommerce, ecommerce.remove.products);

    trackRemoveFromCart({
      products: ecommerce.remove.products.map((product) => toNativeProduct(product, currency)),
      total_value: cartValue(ecommerce.remove.products),
      currency,
    });
  }

  // Transaction (purchase)
  if (ecommerce.purchase?.actionField?.id) {
    const actionField = ecommerce.purchase.actionField;
    const products = ecommerce.purchase.products || [];
    const currency = getCurrency(ecommerce, products);

    trackTransaction({
      transaction_id: actionField.id,
      revenue: parsePrice(actionField.revenue),
      currency,
      payment_method: paymentMethod(ecommerce.purchase),
      total_quantity: products.reduce((total, product) => total + (Number(product.quantity) || 1), 0),
      tax: actionField.tax !== undefined ? parsePrice(actionField.tax) : undefined,
      shipping: actionField.shipping !== undefined ? parsePrice(actionField.shipping) : undefined,
      products: products.map((product) => toNativeProduct(product, currency)),
    });
  }
}

export function enableEnhancedEcommerceTracking() {
  if (window.__iirisEcommercePatched) {
    return;
  }


  const dataLayer = (window.dataLayer = window.dataLayer || []);

 
  dataLayer.forEach((data) => {
    trackViewEventsFromDataLayer(data);
    trackActionEventsFromDataLayer(data);
  });

  const originalPush = dataLayer.push;

  dataLayer.push = function (...args) {
    args.forEach(trackViewEventsFromDataLayer);
    args.forEach(trackActionEventsFromDataLayer);
    return originalPush.apply(this, args);
  };

  window.__iirisEcommercePatched = true;
}
